
import { QueryClient } from '@tanstack/react-query';
import { HttpError } from '@/lib/api/client';

/**
 * Determines whether a failed request is worth retrying.
 *
 * The previous implementation checked `error.message.includes('4')`, which
 * matched ANY message containing the digit "4" — a street number, a price
 * like "$49.99", "Vendor 4" — and therefore disabled retries for a large
 * share of otherwise transient failures, while also retrying real 4xx
 * responses whose message happened to contain a "4".
 *
 * The status code is the reliable signal: 4xx responses are permanent
 * (except 408 and 429), 5xx responses and network failures are transient.
 */
export const isRetryableError = (error: unknown): boolean => {
  if (error instanceof HttpError) {
    // 408 Request Timeout and 429 Too Many Requests are retryable even
    // though they are 4xx responses.
    if (error.status === 408 || error.status === 429) return true;
    if (error.status >= 400 && error.status < 500) return false;
    return true;
  }

  // Network-level failures (fetch rejects with a TypeError) are transient.
  if (error instanceof TypeError) return true;

  return true;
};

export const defaultQueryOptions = {
  staleTime: 0, // 30 minutes
  gcTime:0, // 60 minutes
  retry: (failureCount: number, error: Error) => {
    if (!isRetryableError(error)) {
      return false;
    }
    return failureCount < 3;
  },
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
};

export const defaultMutationOptions = {
  retry: (failureCount: number, error: Error) => {
    if (!isRetryableError(error)) {
      return false;
    }
    return failureCount < 2;
  },
};

export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: defaultQueryOptions,
      mutations: defaultMutationOptions,
    },
  });
};

export const handleQueryError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('Query error:', error.message);
    return error.message;
  }
  console.error('Unknown query error:', error);
  return 'An unknown error occurred';
};

export const handleMutationError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('Mutation error:', error.message);
    return error.message;
  }
  console.error('Unknown mutation error:', error);
  return 'An unknown error occurred';
};

export const isLoadingState = (isLoading: boolean, isFetching: boolean) => {
  return isLoading || isFetching;
};

export const isErrorState = (isError: boolean, error: unknown) => {
  return isError && error !== null;
}; 